import React from 'react';
import { CheckCircle2, Sparkles, Hospital, Phone, Percent, Shield, Wallet, Calendar, IndianRupee, Zap, FileCheck, Settings, TrendingUp, HeartHandshake, Activity } from 'lucide-react';

const CompleteHospitalSetup = () => {
  const covered = [
    { icon: Activity, text: 'Medical Equipment & Diagnostic Systems' },
    { icon: Hospital, text: 'Hospital Beds, Furniture & Fixtures' },
    { icon: Settings, text: 'Modular OT & ICU Setup' },
    { icon: Hospital, text: 'Woodwork & Interior Furnishings' },
    { icon: Zap, text: 'Air Conditioning & Ventilation Systems' },
    { icon: Zap, text: 'Generator & Power Backup Solutions' },
    { icon: Hospital, text: 'Glass Work & Infrastructure Development' },
    { icon: HeartHandshake, text: 'Nursing Stations, Reception & Waiting Areas' },
    { icon: CheckCircle2, text: 'Other Essential Hospital Setup Requirements' }
  ];

  const benefits = [
    { icon: Percent, text: 'Up to 100% Project Financing*', color: 'from-green-500 to-green-600' },
    { icon: Shield, text: 'No Collateral Required*', color: 'from-blue-500 to-blue-600' },
    { icon: Wallet, text: 'Single Loan for Complete Hospital Setup', color: 'from-purple-500 to-purple-600' },
    { icon: Calendar, text: 'Flexible Repayment Tenure Up to 7 Years', color: 'from-green-500 to-emerald-600' },
    { icon: IndianRupee, text: 'Competitive Interest Rates', color: 'from-red-500 to-red-600' },
    { icon: Zap, text: 'Quick Processing & Faster Approvals', color: 'from-orange-500 to-orange-600' },
    { icon: FileCheck, text: 'End-to-End Documentation Support', color: 'from-blue-600 to-cyan-600' },
    { icon: Settings, text: 'Customized Financing Solutions Based on Your Project', color: 'from-pink-500 to-pink-600' }
  ];

  const bottomBadges = [
    { icon: Percent, text: 'Up to 100% Project Financing', color: 'bg-green-500' },
    { icon: Shield, text: 'No Collateral Required', color: 'bg-blue-500' },
    { icon: Zap, text: 'Quick Approvals & Faster Processing', color: 'bg-purple-500' },
    { icon: HeartHandshake, text: 'End-to-End Support', color: 'bg-pink-500' },
    { icon: Sparkles, text: 'Hassle-Free Experience', color: 'bg-orange-500' }
  ];

  const missionTaglines = [
    { icon: TrendingUp, title: 'Empowering Hospitals', subtitle: 'Building a Healthier Tomorrow', color: 'text-green-600' },
    { icon: Hospital, title: 'Smart Financing for', subtitle: 'Stronger Healthcare Infrastructure', color: 'text-blue-600' },
    { icon: HeartHandshake, title: 'Partnering in Your Mission', subtitle: 'to Save Lives', color: 'text-amber-600' }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-amber-50 via-white to-blue-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-600 via-amber-500 to-blue-600"></div>
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-amber-200/40 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Hospital className="w-4 h-4" />
            Complete Hospital Setup Financing
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            All Under <span className="bg-gradient-to-r from-blue-600 to-amber-500 bg-clip-text text-transparent">One Loan</span>
          </h2>
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-5 py-2 rounded-full text-base font-semibold mb-6">
            <Sparkles className="w-5 h-5" />
            We Fund Startup Hospitals
          </div>
          <p className="text-xl md:text-2xl text-gray-700 font-medium leading-relaxed">
            Start your dream hospital <span className="text-blue-600 font-bold">without blocking your own capital</span>
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* What's Covered Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 border border-gray-100 hover:shadow-3xl transition-all duration-500 hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Hospital className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">What's Covered Under One Loan?</h3>
                <p className="text-sm text-gray-600">Complete infrastructure financing</p>
              </div>
            </div>

            <div className="space-y-3">
              {covered.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div
                    key={index}
                    className="group flex items-center gap-3 bg-gradient-to-r from-blue-50 to-transparent rounded-lg p-3 hover:from-blue-100 transition-all duration-300"
                  >
                    <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-800 font-medium text-sm md:text-base">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Key Benefits Card */}
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-3xl shadow-2xl p-8 md:p-10 text-white relative overflow-hidden hover:shadow-3xl transition-all duration-500 hover:-translate-y-1">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/20 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Key Benefits</h3>
                  <p className="text-sm text-blue-200">Why choose our financing</p>
                </div>
              </div>

              <div className="space-y-3">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div
                      key={index}
                      className="group flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/10 hover:bg-white/15 hover:border-amber-400/30 transition-all duration-300"
                    >
                      <div className={`w-9 h-9 bg-gradient-to-br ${benefit.color} rounded-full flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-white font-medium text-sm md:text-base">{benefit.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Badges - Quick Highlights */}
        <div className="mt-12 max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-6 md:p-8 shadow-xl">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {bottomBadges.map((badge, index) => {
                const Icon = badge.icon;
                return (
                  <div key={index} className="flex flex-col items-center text-center group">
                    <div className={`w-14 h-14 ${badge.color} rounded-full flex items-center justify-center shadow-lg mb-3 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <p className="text-white text-xs md:text-sm font-semibold leading-tight">{badge.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Insight Quote */}
        <div className="mt-12 max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-amber-100 via-white to-blue-100 rounded-2xl p-8 md:p-10 border-l-4 border-amber-500 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="text-5xl text-amber-500 leading-none flex-shrink-0">💡</div>
              <p className="text-lg md:text-xl text-gray-800 leading-relaxed">
                <span className="font-semibold">Preserve your working capital</span> for operations, staffing, marketing, and patient care while your hospital{' '}
                <span className="text-blue-600 font-semibold">grows and generates revenue</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Mission Taglines */}
        <div className="mt-12 max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {missionTaglines.map((mission, index) => {
              const Icon = mission.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl mb-4">
                    <Icon className={`w-8 h-8 ${mission.color}`} />
                  </div>
                  <h4 className={`font-bold text-lg ${mission.color} mb-1`}>{mission.title}</h4>
                  <p className="text-gray-700 font-medium">{mission.subtitle}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4 items-center bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 max-w-4xl">
            <div className="flex-1 text-left">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                Contact Us Today
              </h3>
              <p className="text-gray-600">
                For a personalized consultation and discover how easily your hospital project can become a reality.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap"
              >
                Get Started
              </button>
              <a
                href="tel:+919703850009"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompleteHospitalSetup;
